import React, { useEffect, useState } from "react";

import { useQuery } from "@apollo/client";
import { Box, Button, Dialog, Typography } from "@mui/material";
import { differenceInCalendarDays, format } from "date-fns";
import { useLocation, useNavigate } from "react-router-dom";
import { withStyles } from "tss-react/mui";

import OjieWithRedFlagIcon from "@/components/Icons/OjieWithRedFlagIcon";
import { graphql } from "@/gql";
import { SubscriptionStatus } from "@/gql/graphql";

const renewalReminderQuery = graphql(`
  query RenewalReminder {
    coachViewer {
      id
      subscriptionStatus
      subscriptionWillEnd
      subscriptionCurrentPeriodEnd
    }
  }
`);

// Shown once per local calendar day per device (localStorage), scoped by coach id so a
// shared device with several accounts reminds each of them.
const seenKey = (coachId: string) => `renewalReminderSeenOn:${coachId}`;
const today = () => format(new Date(), "yyyy-MM-dd");

// Pages where the reminder is redundant — the coach is already looking at renewal UI.
const EXEMPT_PATHS = [
  "/subscription",
  "/payment-failed",
  "/payment-successful",
];

const StyledOjieWithRedFlagIcon = withStyles(OjieWithRedFlagIcon, {
  root: {
    width: 130,
    height: 130,
  },
});

// Full-page daily nudge for one-time payers whose paid period is ending (subscriptionWillEnd,
// flagged 3 days out by the backend) or already lapsed into the past_due grace window.
// Suspended coaches never see this — GlobalListeners redirects them to /payment-failed.
const RenewalReminder: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  // network-only: the cache is persisted across sessions (ApolloPersistGate) and no other query
  // selects these fields, so a cached subscriptionWillEnd could stay stale forever — a daily
  // full-page nudge must fire on server truth only.
  const { data } = useQuery(renewalReminderQuery, {
    fetchPolicy: "network-only",
  });

  const coach = data?.coachViewer;
  const isPastDue = coach?.subscriptionStatus === SubscriptionStatus.PastDue;
  const needsRenewal = Boolean(coach?.subscriptionWillEnd) || isPastDue;
  const exempt = EXEMPT_PATHS.some((path) => pathname.startsWith(path));
  const coachId = coach?.id;

  // Stamp the device when the reminder is shown (not when dismissed) so a mid-session
  // refresh doesn't re-trigger it.
  useEffect(() => {
    if (coachId == null || !needsRenewal || exempt) return;
    if (localStorage.getItem(seenKey(coachId)) === today()) return;

    localStorage.setItem(seenKey(coachId), today());
    setOpen(true);
  }, [coachId, needsRenewal, exempt]);

  if (!open) return null;

  const endDate = coach?.subscriptionCurrentPeriodEnd
    ? format(new Date(coach.subscriptionCurrentPeriodEnd), "dd MMM, yyyy")
    : null;
  // Calendar-day countdown for the headline: "today", "tomorrow", "in N days".
  const daysLeft = coach?.subscriptionCurrentPeriodEnd
    ? differenceInCalendarDays(
        new Date(coach.subscriptionCurrentPeriodEnd),
        new Date()
      )
    : null;
  const endsPhrase =
    daysLeft == null || daysLeft < 0
      ? "soon"
      : daysLeft === 0
      ? "today"
      : daysLeft === 1
      ? "tomorrow"
      : `in ${daysLeft} days`;

  const handleRenew = () => {
    setOpen(false);
    navigate("/subscription");
  };

  return (
    <Dialog fullScreen open>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        textAlign="center"
        height="100%"
        padding={8}
      >
        <StyledOjieWithRedFlagIcon />
        <Typography variant="h4" fontWeight={700} marginTop={6}>
          {isPastDue ? "Your plan has ended" : `Your plan ends ${endsPhrase}`}
        </Typography>
        <Typography color="textSecondary" marginTop={2} maxWidth={480}>
          {isPastDue
            ? `Your access ended${
                endDate ? ` on ${endDate}` : ""
              } — renew now to avoid your account being suspended.`
            : `Your access ends${
                endDate ? ` on ${endDate}` : " soon"
              }. Renew now to keep working with your clients without interruption.`}
        </Typography>
        <Button variant="contained" sx={{ marginTop: 6 }} onClick={handleRenew}>
          Renew now
        </Button>
        <Button
          variant="text"
          color="inherit"
          sx={{ marginTop: 2, color: "text.secondary" }}
          onClick={() => setOpen(false)}
        >
          Remind me tomorrow
        </Button>
      </Box>
    </Dialog>
  );
};

export default RenewalReminder;
